import { Link, useLoaderData, useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import Avatar from "@mui/material/Avatar";
import SvgIcon from "@mui/material/SvgIcon";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";

import { Caption } from "..";
import defaultImage from "../../images/default-image.png";
import { CAMPAIGN } from "../../config/RESTAPI/";

import IconBag from "../../images/icon-bag.png";

const CampaignList = () => {
  const { data } = useLoaderData();
  const { rows, pag } = data;
  const navigate = useNavigate();

  const handleDelete = (id) => {
    Swal.fire({
      title: "Warning",
      showCancelButton: true,
      icon: "warning",
      text: "Are you sure you want to delete this project?",
      confirmButtonText: "Proceed",
      cancelButtonText: "Cancel",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((response) => {
      const { isConfirmed } = response;

      if (isConfirmed) {
        CAMPAIGN.Remove(id).then(() => {
          navigate("/campaigns");
        });
      }
    });
  };

  const ArrBack = () => (
    <SvgIcon width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" sx={{ fill: "none" }}>
      <path d="M15.5 5L8.5 12L15.5 19" stroke="#ADADAD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );

  const ArrNext = () => (
    <SvgIcon width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" sx={{ fill: "none" }}>
      <path d="M8.5 5L15.5 12L8.5 19" stroke="#ADADAD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );

  return (
    <Box>
      <Caption title="Manage Campaign" Icon={<img src={IconBag} alt="" />} />

      <Box sx={{ mb: 2, display: "flex", alignItems: "baseline" }}>
        <Box sx={{ mx: 1, display: "flex", width: 15 }}>
          <Badge color="error" badgeContent="!" sx={{ display: "flex" }} />
        </Box>

        <Box>
          <Typography variant="accordion.title" component="p" sx={{ fontSize: 16 }}>
            If you see this red warning sign in the Options of your campaign, this means your smart contract is not published yet, and your campaign is not
            active. Click the Manage Smart Contract tool and publish your contract to get the campaign running.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 8 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Logo</TableCell>
              <TableCell>Campaign Name</TableCell>
              <TableCell size="small">Date Created</TableCell>
              <TableCell size="small" width={10}>
                Options
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 &&
              rows.map(({ id, name, images, type, createdAt, ContractSetting: { flg_publish } }, key) => (
                <TableRow
                  key={`${name}-${key}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    <Avatar sx={{ width: 88, height: 88, borderRadius: 6 }} src={images?.find(({ main }) => main)?.image || defaultImage} />
                  </TableCell>

                  <TableCell>{name}</TableCell>

                  <TableCell>
                    {moment(createdAt).format("LL")}
                    <br />
                    {moment(createdAt).format("LT")}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          padding: 1,
                          borderRadius: 2.5,
                          "& > *:not(.svg-border)": {
                            display: "flex",
                            mx: 0.625,
                          },
                          "&:hover": {
                            background: "linear-gradient(118.77deg, rgba(255, 255, 255, 0.15) 20.62%, rgba(255, 255, 255, 0) 99.9%)",
                            backdropFilter: "blur(10px)",
                            ".svg-border": {
                              display: "flex",
                            },
                          },
                        }}
                      >
                        <SvgIcon
                          viewBox={null}
                          sx={{
                            position: "absolute",
                            fill: "none",
                            top: 0,
                            left: 0,
                            overflow: "visible",
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                            display: "none",
                          }}
                          className="svg-border"
                        >
                          <rect x="0.5" y="0.5" width="100%" height="100%" rx="9.5" stroke="url(#paint1_linear_1111_3474)" strokeOpacity="0.3" />
                        </SvgIcon>

                        <Tooltip title="Edit campaign" arrow>
                          <Link to={`/campaigns/edit/${id}`}>
                            <SvgIcon
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              sx={{
                                fill: "var(--default-text-color)",
                              }}
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M20.9028 3.84217C19.7799 2.71928 17.9651 2.71928 16.8422 3.84217L3.84217 16.8422C2.71928 17.9651 2.71928 19.7799 3.84217 20.9028C4.96506 22.0257 6.77994 22.0257 7.90283 20.9028L19.4066 9.39904C19.4087 9.39699 19.4108 9.39492 19.4129 9.39283C19.415 9.39075 19.4171 9.38866 19.4191 9.38655L20.9028 7.90283C22.0257 6.77994 22.0257 4.96506 20.9028 3.84217ZM18.8825 7.80181L19.8422 6.84217C20.3793 6.30506 20.3793 5.43994 19.8422 4.90283C19.3051 4.36572 18.4399 4.36572 17.9028 4.90283L16.9432 5.86247L18.8825 7.80181ZM15.8825 6.92313L4.90283 17.9028C4.36572 18.4399 4.36572 19.3051 4.90283 19.8422C5.43994 20.3793 6.30506 20.3793 6.84217 19.8422L17.8219 8.86247L15.8825 6.92313Z"
                              />
                            </SvgIcon>
                          </Link>
                        </Tooltip>

                        {type === 2 && (
                          <Tooltip title="Template" arrow>
                            <Link to={`/builder/${id}/`}>
                              <SvgIcon
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                sx={{
                                  fill: "none",
                                  path: {
                                    stroke: "var(--default-text-color)",
                                  },
                                }}
                              >
                                <path
                                  d="M20.642 6.19914C20.5761 6.03275 20.4308 5.91076 20.2556 5.87451C20.0801 5.8382 19.8985 5.89265 19.772 6.01921L17.03 8.76119L15.5558 8.44422L15.2389 6.97005L17.9809 4.22804C18.1074 4.10148 18.1618 3.91972 18.1256 3.74446C18.0893 3.56917 17.9673 3.42394 17.8009 3.35792C15.9184 2.61141 13.7769 3.05407 12.3452 4.4857C11.656 5.1749 11.1827 6.04055 10.9765 6.98904C10.8002 7.80051 10.8249 8.63895 11.0465 9.43394L3.72897 16.7515C3.25886 17.2215 3 17.8465 3 18.5113C3 19.1761 3.25886 19.801 3.72893 20.2711C4.199 20.7411 4.82394 21 5.48871 21C6.15345 21 6.77845 20.7411 7.24849 20.2711L14.5661 12.9535C15.3611 13.1751 16.1995 13.1998 17.0109 13.0235C17.9594 12.8173 18.8251 12.344 19.5143 11.6548C20.946 10.2232 21.3886 8.08166 20.642 6.19914Z"
                                  strokeWidth="1.5"
                                />
                              </SvgIcon>
                            </Link>
                          </Tooltip>
                        )}

                        <Tooltip title="Manage Smart Contract" arrow>
                          <Link to={`/campaigns/${id}/contract`}>
                            {!flg_publish ? (
                              <Badge color="error" badgeContent="!">
                                <SvgIcon
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                  sx={{
                                    fill: "none",
                                    stroke: "var(--default-text-color)",
                                    strokeWidth: 1.5,
                                    cursor: "pointer",
                                  }}
                                >
                                  <path d="M20.091 9.70298C18.2819 9.70298 17.5422 8.4147 18.4418 6.83453C18.9615 5.91864 18.6517 4.75113 17.7421 4.22777L16.013 3.23136C15.2234 2.75832 14.2039 3.04013 13.7341 3.83524L13.6242 4.02647C12.7246 5.60664 11.2454 5.60664 10.3358 4.02647L10.2259 3.83524C9.77611 3.04013 8.75662 2.75832 7.96702 3.23136L6.23788 4.22777C5.32834 4.75113 5.01849 5.92871 5.53823 6.8446C6.44778 8.4147 5.70815 9.70298 3.89905 9.70298C2.85957 9.70298 2 10.5585 2 11.6153V13.3867C2 14.4334 2.84958 15.299 3.89905 15.299C5.70815 15.299 6.44778 16.5873 5.53823 18.1674C5.01849 19.0833 5.32834 20.2508 6.23788 20.7742L7.96702 21.7706C8.75662 22.2436 9.77611 21.9618 10.2459 21.1667L10.3558 20.9755C11.2554 19.3953 12.7346 19.3953 13.6442 20.9755L13.7541 21.1667C14.2239 21.9618 15.2434 22.2436 16.033 21.7706L17.7621 20.7742C18.6717 20.2508 18.9815 19.0732 18.4618 18.1674C17.5522 16.5873 18.2919 15.299 20.101 15.299C21.1404 15.299 22 14.4435 22 13.3867V11.6153C21.99 10.5685 21.1404 9.70298 20.091 9.70298ZM11.995 15.772C10.2059 15.772 8.74663 14.3026 8.74663 12.501C8.74663 10.6994 10.2059 9.22994 11.995 9.22994C13.7841 9.22994 15.2434 10.6994 15.2434 12.501C15.2434 14.3026 13.7841 15.772 11.995 15.772Z" />
                                </SvgIcon>
                              </Badge>
                            ) : (
                              <SvgIcon
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                sx={{
                                  fill: "none",
                                  stroke: "var(--default-text-color)",
                                  strokeWidth: 1.5,
                                  cursor: "pointer",
                                }}
                              >
                                <path d="M20.091 9.70298C18.2819 9.70298 17.5422 8.4147 18.4418 6.83453C18.9615 5.91864 18.6517 4.75113 17.7421 4.22777L16.013 3.23136C15.2234 2.75832 14.2039 3.04013 13.7341 3.83524L13.6242 4.02647C12.7246 5.60664 11.2454 5.60664 10.3358 4.02647L10.2259 3.83524C9.77611 3.04013 8.75662 2.75832 7.96702 3.23136L6.23788 4.22777C5.32834 4.75113 5.01849 5.92871 5.53823 6.8446C6.44778 8.4147 5.70815 9.70298 3.89905 9.70298C2.85957 9.70298 2 10.5585 2 11.6153V13.3867C2 14.4334 2.84958 15.299 3.89905 15.299C5.70815 15.299 6.44778 16.5873 5.53823 18.1674C5.01849 19.0833 5.32834 20.2508 6.23788 20.7742L7.96702 21.7706C8.75662 22.2436 9.77611 21.9618 10.2459 21.1667L10.3558 20.9755C11.2554 19.3953 12.7346 19.3953 13.6442 20.9755L13.7541 21.1667C14.2239 21.9618 15.2434 22.2436 16.033 21.7706L17.7621 20.7742C18.6717 20.2508 18.9815 19.0732 18.4618 18.1674C17.5522 16.5873 18.2919 15.299 20.101 15.299C21.1404 15.299 22 14.4435 22 13.3867V11.6153C21.99 10.5685 21.1404 9.70298 20.091 9.70298ZM11.995 15.772C10.2059 15.772 8.74663 14.3026 8.74663 12.501C8.74663 10.6994 10.2059 9.22994 11.995 9.22994C13.7841 9.22994 15.2434 10.6994 15.2434 12.501C15.2434 14.3026 13.7841 15.772 11.995 15.772Z" />
                              </SvgIcon>
                            )}
                          </Link>
                        </Tooltip>

                        <Tooltip title="Widgets" arrow>
                          <Link to={`/campaigns/${id}/widget`}>
                            <SvgIcon
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              sx={{
                                fill: "var(--default-text-color)",
                              }}
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M20.2422 2H3.75781C2.78855 2 2 2.78855 2 3.75781V20.2422C2 21.2114 2.78855 22 3.75781 22H20.2422C21.2114 22 22 21.2114 22 20.2422V3.75781C22 2.78855 21.2114 2 20.2422 2ZM20.8281 20.2422C20.8281 20.5653 20.5653 20.8281 20.2422 20.8281H3.75781C3.43473 20.8281 3.17188 20.5653 3.17188 20.2422V7.85938H20.8281V20.2422ZM20.8281 6.6875H3.17188V3.75781C3.17188 3.43473 3.43473 3.17188 3.75781 3.17188H20.2422C20.5653 3.17188 20.8281 3.43473 20.8281 3.75781V6.6875ZM6.96928 14.3438L8.89869 12.4144C9.12752 12.1855 9.12752 11.8145 8.89869 11.5857C8.6699 11.3569 8.29889 11.3569 8.07006 11.5857L5.72631 13.9294C5.49748 14.1583 5.49748 14.5292 5.72631 14.7581L8.07006 17.1018C8.29885 17.3306 8.66986 17.3306 8.89869 17.1018C9.12752 16.873 9.12752 16.502 8.89869 16.2732L6.96928 14.3438ZM15.9299 11.5857L18.2737 13.9294C18.5026 14.1583 18.5026 14.5292 18.2737 14.7581L15.93 17.1018C15.7012 17.3306 15.3301 17.3306 15.1013 17.1018C14.8725 16.873 14.8725 16.502 15.1013 16.2732L17.0308 14.3438L15.1013 12.4143C14.8725 12.1855 14.8725 11.8145 15.1013 11.5857C15.3301 11.3569 15.7012 11.3569 15.9299 11.5857ZM13.3573 10.2724C13.0502 10.17 12.7185 10.336 12.6162 10.643L10.2724 17.6743C10.1701 17.9813 10.336 18.3131 10.643 18.4154C10.9501 18.5178 11.2819 18.3518 11.3842 18.0449L13.7279 11.0136C13.8302 10.7066 13.6643 10.3747 13.3573 10.2724ZM4.96875 4.34375H6.14062C6.46422 4.34375 6.72656 4.60609 6.72656 4.92969C6.72656 5.25328 6.46422 5.51562 6.14062 5.51562H4.96875C4.64516 5.51562 4.38281 5.25328 4.38281 4.92969C4.38281 4.60609 4.64516 4.34375 4.96875 4.34375ZM9.65625 4.34375H8.48438C8.16078 4.34375 7.89844 4.60609 7.89844 4.92969C7.89844 5.25328 8.16078 5.51562 8.48438 5.51562H9.65625C9.97984 5.51562 10.2422 5.25328 10.2422 4.92969C10.2422 4.60609 9.97984 4.34375 9.65625 4.34375ZM12 4.34375H13.1719C13.4955 4.34375 13.7578 4.60609 13.7578 4.92969C13.7578 5.25328 13.4955 5.51562 13.1719 5.51562H12C11.6764 5.51562 11.4141 5.25328 11.4141 4.92969C11.4141 4.60609 11.6764 4.34375 12 4.34375Z"
                              />
                            </SvgIcon>
                          </Link>
                        </Tooltip>

                        <Tooltip title="Statistics" arrow>
                          <Link to={`/productivity/statistics/${id}`}>
                            <SvgIcon
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              sx={{
                                fill: "none",
                                path: {
                                  stroke: "var(--default-text-color)",
                                },
                              }}
                            >
                              <path
                                d="M10.1103 11.1504H7.4603C6.8303 11.1504 6.32031 11.6603 6.32031 12.2903V17.4103H10.1103V11.1504V11.1504Z"
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12.7616 6.6001H11.2415C10.6115 6.6001 10.1016 7.11011 10.1016 7.74011V17.4001H13.8916V7.74011C13.8916 7.11011 13.3916 6.6001 12.7616 6.6001Z"
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M16.5484 12.8501H13.8984V17.4001H17.6884V13.9901C17.6784 13.3601 17.1684 12.8501 16.5484 12.8501Z"
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </SvgIcon>
                          </Link>
                        </Tooltip>

                        <Tooltip title="Delete this campaign" arrow>
                          <SvgIcon
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            sx={{
                              fill: "none",
                              path: {
                                stroke: "var(--default-text-color)",
                              },
                              cursor: "pointer",
                            }}
                            onClick={() => handleDelete(id)}
                          >
                            <path
                              d="M20.5 6.50004C20.5 6.50004 13.6911 6 10.5367 6C8.66667 6 3.5 6.5 3.5 6.5"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.5 5.5L8.72 3.67677C8.88 2.71717 9 2 10.69 2H13.31C15 2 15.13 2.75758 15.28 3.68687L15.5 6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M18 9L17.4307 18.3966C17.3343 19.8616 17.2555 21 14.8117 21H9.18832C6.74453 21 6.66569 19.8616 6.56934 18.3966L6 9"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path d="M10.5 17H13.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9.5 13H14.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </SvgIcon>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>

      {pag.last_page > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            sx={{ display: "flex" }}
            count={pag.last_page}
            page={pag.current_page}
            renderItem={(item) => <PaginationItem component={Link} slots={{ previous: ArrBack, next: ArrNext }} to={`/campaigns/${item.page}`} {...item} />}
          />
        </Box>
      )}
    </Box>
  );
};

export default CampaignList;
